import { useCookieConsentStore } from '~/stores/cookieConsent'

export default defineNuxtPlugin((nuxtApp) => {
  const cookieStore = useCookieConsentStore()

  // Initialize cookie store from localStorage
  cookieStore.initialize()

  // ── Consent Mode V2 ──────────────────────────────────────────────────────
  // Must be pushed to dataLayer BEFORE GTM loads so it can act on the default.
  window.dataLayer = window.dataLayer || []
  // Use arguments form so GA4 parses commands correctly.
  window.gtag = function gtag() {
    window.dataLayer.push(arguments as unknown as Record<string, unknown>)
  }
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  })

  // Helper: map stored preferences → Consent Mode V2 update
  const applyConsentModeUpdate = () => {
    const prefs = cookieStore.preferences
    window.gtag('consent', 'update', {
      analytics_storage: prefs.googleAnalytics ? 'granted' : 'denied',
      ad_storage: prefs.marketing ? 'granted' : 'denied',
      ad_user_data: prefs.marketing ? 'granted' : 'denied',
      ad_personalization: prefs.marketing ? 'granted' : 'denied',
    })
  }

  // If the user already made a choice (returning visitor), apply it immediately
  // so GTM picks it up before firing any tags.
  if (cookieStore.isAccepted) {
    applyConsentModeUpdate()
  }

  // ── GTM (always loaded) ──────────────────────────────────────────────────
  let gtmLoaded = false
  const loadGTM = () => {
    if (gtmLoaded) return
    const config = useRuntimeConfig()
    const containerId = config.public.GTM_CONTAINER_ID
    if (!containerId) return

    const script = document.createElement('script')
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${containerId}');
    `
    document.head.appendChild(script)

    const noscript = document.createElement('noscript')
    const iframe = document.createElement('iframe')
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`
    iframe.height = '0'
    iframe.width = '0'
    iframe.style.display = 'none'
    iframe.style.visibility = 'hidden'
    noscript.appendChild(iframe)
    document.body.appendChild(noscript)

    gtmLoaded = true
  }

  // ── GA4 (consent-gated) ──────────────────────────────────────────────────
  let ga4Loaded = false
  const loadGA4 = () => {
    const config = useRuntimeConfig()
    const id = config.public.GA_MEASUREMENT_ID
    if (!cookieStore.preferences.googleAnalytics || !id) return
    if (ga4Loaded) return

    if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${id}"]`)) {
      ga4Loaded = true
      return
    }

    const gaScript = document.createElement('script')
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${id}`
    gaScript.async = true
    document.head.appendChild(gaScript)

    window.gtag('js', new Date())
    window.gtag('config', id)

    ga4Loaded = true
  }

  // ── Google Maps (consent-gated) ──────────────────────────────────────────
  const loadGoogleMaps = () => {
    const config = useRuntimeConfig()
    const apiKey = config.public.GOOGLE_MAPS_API_KEY
    if (!cookieStore.preferences.googleMaps || !apiKey) return

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  }

  // ── YouTube (consent-gated) ──────────────────────────────────────────────
  const loadYouTube = () => {
    if (!cookieStore.preferences.youtube) return

    const script = document.createElement('script')
    script.src = 'https://www.youtube.com/iframe_api'
    script.async = true
    document.head.appendChild(script)
  }

  // ── Matomo (always loaded) ───────────────────────────────────────────────
  let matomoLoaded = false
  const loadMatomo = () => {
    if (matomoLoaded) return
    const config = useRuntimeConfig()
    const matomoUrl = config.public.MATOMO_URL as string
    if (!matomoUrl) return
    if (document.querySelector('script[src*="matomo.js"]')) {
      matomoLoaded = true
      return
    }

    window._paq = window._paq || []
    window._paq.push(['setTrackerUrl', matomoUrl + 'matomo.php'])
    window._paq.push(['setSiteId', 1])
    window._paq.push(['enableLinkTracking'])
    window._paq.push(['trackPageView'])

    const script = document.createElement('script')
    script.src = matomoUrl + 'matomo.js'
    script.async = true
    script.defer = true
    document.head.appendChild(script)

    matomoLoaded = true
  }

  // ── Microsoft Bing (consent-gated) ───────────────────────────────────────
  const loadMicrosoftBing = () => {
    const config = useRuntimeConfig()
    const bingId = config.public.BING_ID
    if (!cookieStore.preferences.microsoftBing || !bingId) return

    const script = document.createElement('script')
    script.innerHTML = `
      (function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){var o={ti:"${bingId}"};
      o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")},n=d.createElement(t),n.src=r,
      n.async=1,n.onload=n.onreadystatechange=function(){var s=this.readyState;
      s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)},
      i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)})
      (window,document,"script","//bat.bing.com/bat.js","uetq");
    `
    document.head.appendChild(script)
  }

  // ── Remove consent-gated scripts only ────────────────────────────────────
  // GTM and Matomo are intentionally excluded — they stay loaded regardless.
  const removeTrackingScripts = () => {
    const trackingScripts = document.querySelectorAll(
      'script[src*="maps.googleapis.com"], script[src*="youtube.com"], script[src*="bing.com"]'
    )
    trackingScripts.forEach((script) => script.remove())

    // Remove GA4 inline init and UET scripts
    document.querySelectorAll('script').forEach((script) => {
      if (script.innerHTML.includes('UET')) script.remove()
    })

    ga4Loaded = false

    // Remove the GA4 src script
    const ga4Script = document.querySelector('script[src*="googletagmanager.com/gtag/js"]')
    if (ga4Script) ga4Script.remove()
  }

  // ── Always load GTM and Matomo immediately ───────────────────────────────
  loadGTM()
  loadMatomo()

  // ── Watch consent changes ────────────────────────────────────────────────
  watch(
    () => cookieStore.preferences,
    (newPreferences) => {
      // Update Consent Mode V2 whenever preferences change (user made a choice)
      if (cookieStore.isAccepted) {
        applyConsentModeUpdate()

        if (newPreferences.googleAnalytics) loadGA4()
        if (newPreferences.googleMaps) loadGoogleMaps()
        if (newPreferences.youtube) loadYouTube()
        if (newPreferences.microsoftBing) loadMicrosoftBing()

        if (
          !newPreferences.googleAnalytics &&
          !newPreferences.googleMaps &&
          !newPreferences.youtube &&
          !newPreferences.microsoftBing
        ) {
          removeTrackingScripts()
        }
      } else {
        removeTrackingScripts()
      }
    },
    { deep: true, immediate: true }
  )

  // ── SPA page tracking ────────────────────────────────────────────────────
  // Matomo is always loaded — track every page navigation unconditionally.
  nuxtApp.hook('page:finish', () => {
    if (window._paq) {
      window._paq.push(['setCustomUrl', window.location.href])
      window._paq.push(['setDocumentTitle', document.title])
      window._paq.push(['trackPageView'])
    }
  })

  return {
    provide: {
      loadTrackingScripts: () => {
        if (cookieStore.preferences.googleAnalytics) loadGA4()
        if (cookieStore.preferences.googleMaps) loadGoogleMaps()
        if (cookieStore.preferences.youtube) loadYouTube()
        if (cookieStore.preferences.microsoftBing) loadMicrosoftBing()
      },
      removeTrackingScripts,
    },
  }
})

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[]
    gtag: (...args: any[]) => void
    _paq: any[]
  }
}